// The Envelope: the signed, per-recipient encrypted unit published to the relay
// (ARCHITECTURE.md §4, §13.3).
//
//   Envelope { authorId, recipientId, version, content, keyWrap, signature }
//
// The profile is sealed ONCE with a content key K (sealProfileRecord); K is then
// wrapped separately per recipient (buildEnvelope). The author signs over
// (recipientId ‖ version ‖ content ‖ keyWrap), so a recipient can prove the
// update is authentic, intended for them, and tied to a specific version — even
// though it travelled through a relay that can read none of it.

import { decodeCircleId, type KeyBundle } from './keys';
import { sign, verify } from './sign';
import {
  generateContentKey,
  openContent,
  sealContent,
  unwrapContentKey,
  wrapContentKey,
  type Sealed,
} from './seal';
import type { Profile, ProfileRecord } from './profile';
import type { Friend } from './friends';
import type { Group } from './groups';
import { base64urlToBytes, bytesToBase64url, bytesToUtf8, concatBytes, utf8ToBytes } from './encoding';

export interface SealedWire {
  iv: string; // base64url
  ct: string; // base64url
}

export interface Envelope {
  v: 1;
  authorId: string;
  recipientId: string;
  version: number;
  content: SealedWire; // sealed ProfileRecord (or BackupRecord for a self-envelope)
  keyWrap: SealedWire; // content key wrapped for recipientId
  signature: string; // base64url
}

/**
 * The full self-backup payload (ARCHITECTURE.md §8): my profile plus my local
 * address book — contacts and groups — so a recovery on a new device restores
 * not just the identity and profile, but the app's whole local state. Sealed
 * and addressed to myself only; friends still receive a plain ProfileRecord,
 * never this. `version` is its own snapshot counter (a timestamp), independent
 * of the ProfileRecord's edit-count version nested inside `profile`.
 */
export interface BackupRecord {
  version: number;
  updatedAt: number;
  profile: ProfileRecord;
  friends: Friend[];
  groups: Group[];
}

function toWire(s: Sealed): SealedWire {
  return { iv: bytesToBase64url(s.iv), ct: bytesToBase64url(s.ciphertext) };
}

function fromWire(w: SealedWire): Sealed {
  return { iv: base64urlToBytes(w.iv), ciphertext: base64urlToBytes(w.ct) };
}

function u64be(n: number): Uint8Array {
  const buf = new ArrayBuffer(8);
  new DataView(buf).setBigUint64(0, BigInt(n));
  return new Uint8Array(buf);
}

/** Length-prefixed encoding so concatenated fields are unambiguous. */
function lenPrefixed(bytes: Uint8Array): Uint8Array {
  const len = new ArrayBuffer(4);
  new DataView(len).setUint32(0, bytes.length);
  return concatBytes(new Uint8Array(len), bytes);
}

/**
 * Canonical bytes the author signs (and the recipient verifies).
 *
 * `authorId` is included so the signature binds BOTH halves of it — the
 * signing key (which ECDSA already ties to the signature) AND the ECDH key
 * used to wrap the content key. Before this, only the signing half was
 * authenticated: something with write access to a stored envelope (a
 * malicious or compromised relay — explicitly in scope, ARCHITECTURE.md §2)
 * could swap the ECDH half of `authorId` without invalidating the signature,
 * silently breaking `unwrapContentKey` for the real recipient. Domain tag
 * bumped to v2 so a v1-signed envelope simply fails verification here rather
 * than being ambiguous about which scheme produced it — the client and relay
 * ship together (see DEPLOY.md), so there's no long-lived mixed-version
 * population to support.
 */
function signingBytes(authorId: string, recipientId: string, version: number, content: Sealed, keyWrap: Sealed): Uint8Array {
  return concatBytes(
    utf8ToBytes('circle/v2/envelope'),
    lenPrefixed(utf8ToBytes(authorId)),
    lenPrefixed(utf8ToBytes(recipientId)),
    u64be(version),
    lenPrefixed(content.iv),
    lenPrefixed(content.ciphertext),
    lenPrefixed(keyWrap.iv),
    lenPrefixed(keyWrap.ciphertext),
  );
}

async function sealJson(payload: unknown): Promise<{ contentKey: Uint8Array; content: Sealed }> {
  const plaintext = utf8ToBytes(JSON.stringify(payload));
  const contentKey = generateContentKey();
  const content = await sealContent(contentKey, plaintext);
  return { contentKey, content };
}

/**
 * Seal a ProfileRecord once with a fresh content key. Reuse the returned key
 * and sealed content to build one Envelope per recipient for the same version.
 */
export function sealProfileRecord(record: ProfileRecord): Promise<{ contentKey: Uint8Array; content: Sealed }> {
  return sealJson(record);
}

/** Seal a BackupRecord (profile + contacts + groups) for the self-envelope. */
export function sealBackupRecord(record: BackupRecord): Promise<{ contentKey: Uint8Array; content: Sealed }> {
  return sealJson(record);
}

/** Build a signed Envelope of already-sealed content for one recipient. */
export async function buildEnvelope(
  authorKeys: KeyBundle,
  authorId: string,
  recipientId: string,
  version: number,
  content: Sealed,
  contentKey: Uint8Array,
): Promise<Envelope> {
  const recipient = decodeCircleId(recipientId);
  const keyWrap = await wrapContentKey(authorKeys.ecdhPrivate, recipient.ecdhPublicRaw, contentKey);
  const signature = await sign(authorKeys.signPrivate, signingBytes(authorId, recipientId, version, content, keyWrap));
  return {
    v: 1,
    authorId,
    recipientId,
    version,
    content: toWire(content),
    keyWrap: toWire(keyWrap),
    signature: bytesToBase64url(signature),
  };
}

/** Convenience: seal + build a single Envelope for one recipient. */
export async function buildEnvelopeForRecipient(
  authorKeys: KeyBundle,
  authorId: string,
  recipientId: string,
  record: ProfileRecord,
): Promise<Envelope> {
  const { contentKey, content } = await sealProfileRecord(record);
  return buildEnvelope(authorKeys, authorId, recipientId, record.version, content, contentKey);
}

export class EnvelopeError extends Error {}

/** Verify signature + integrity and decrypt an Envelope's content to bytes. */
async function decryptEnvelope(myKeys: KeyBundle, myId: string, env: Envelope): Promise<Uint8Array> {
  if (env.recipientId !== myId) throw new EnvelopeError('Enveloppe destinée à un autre destinataire');

  const author = decodeCircleId(env.authorId);
  const content = fromWire(env.content);
  const keyWrap = fromWire(env.keyWrap);

  const ok = await verify(
    author.signPublicRaw,
    base64urlToBytes(env.signature),
    signingBytes(env.authorId, env.recipientId, env.version, content, keyWrap),
  );
  if (!ok) throw new EnvelopeError('Signature invalide');

  const contentKey = await unwrapContentKey(myKeys.ecdhPrivate, author.ecdhPublicRaw, keyWrap);
  return openContent(contentKey, content);
}

/**
 * Verify and open an Envelope addressed to me. Throws EnvelopeError on any
 * integrity failure (wrong recipient, bad signature, version mismatch).
 */
export async function openEnvelope(myKeys: KeyBundle, myId: string, env: Envelope): Promise<ProfileRecord> {
  const bytes = await decryptEnvelope(myKeys, myId, env);
  const record = JSON.parse(bytesToUtf8(bytes)) as ProfileRecord;
  if (record.version !== env.version) throw new EnvelopeError('Version incohérente');
  return record;
}

/**
 * Verify and open a self-envelope as a BackupRecord (profile + contacts +
 * groups). Also understands a self-envelope sealed by a pre-backup-expansion
 * client — a flat `{ version, updatedAt, profile: Profile }` ProfileRecord,
 * with no `friends`/`groups` — and lifts it into a BackupRecord with an empty
 * address book, so upgrading the app never loses a profile already backed up.
 */
export async function openBackupEnvelope(myKeys: KeyBundle, myId: string, env: Envelope): Promise<BackupRecord> {
  const bytes = await decryptEnvelope(myKeys, myId, env);
  const raw = JSON.parse(bytesToUtf8(bytes)) as Record<string, unknown>;
  if (raw.version !== env.version) throw new EnvelopeError('Version incohérente');

  const nestedProfile = raw.profile as Record<string, unknown> | undefined;
  const isCurrentFormat = !!nestedProfile && 'profile' in nestedProfile && 'version' in nestedProfile;
  if (isCurrentFormat) return raw as unknown as BackupRecord;

  const legacyProfile: ProfileRecord = {
    version: raw.version as number,
    updatedAt: raw.updatedAt as number,
    profile: raw.profile as Profile,
  };
  return { version: raw.version as number, updatedAt: raw.updatedAt as number, profile: legacyProfile, friends: [], groups: [] };
}
